<template>
  <div class="notifications-page">
    <div class="page-header">
      <h1>Уведомления</h1>
      <button
        v-if="unreadCount > 0"
        class="mark-all-btn"
        @click="markAllAsRead"
      >
        <i class="pi pi-check-double"></i>
        Отметить все прочитанными
      </button>
    </div>

    <div class="filters-bar">
      <div class="search-field">
        <i class="pi pi-search"></i>
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Поиск по заголовку или сообщению..."
          class="search-input"
        />
      </div>

      <div class="filter-buttons">
        <button
          class="filter-btn"
          :class="{ active: filterType === 'all' }"
          @click="filterType = 'all'"
        >
          Все ({{ totalCount }})
        </button>
        <button
          class="filter-btn"
          :class="{ active: filterType === 'unread' }"
          @click="filterType = 'unread'"
        >
          Непрочитанные ({{ unreadCount }})
        </button>
        <button
          class="filter-btn"
          :class="{ active: filterType === 'read' }"
          @click="filterType = 'read'"
        >
          Прочитанные ({{ readCount }})
        </button>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <i class="pi pi-spin pi-spinner"></i>
      <p>Загрузка...</p>
    </div>

    <div v-else-if="filteredNotifications.length === 0" class="empty-state">
      <i class="pi pi-bell-slash"></i>
      <h3>Нет уведомлений</h3>
      <p>Когда появятся новые уведомления, они будут отображаться здесь</p>
    </div>

    <div v-else class="notifications-list">
      <div
        v-for="notification in paginatedNotifications"
        :key="notification.id"
        class="notification-card"
        :class="{ unread: !notification.isRead }"
      >
        <div class="notification-icon">
          <i :class="getNotificationIcon(notification.title)"></i>
        </div>
        <div class="notification-content">
          <div class="notification-header">
            <span class="notification-title">{{ notification.title }}</span>
            <span class="notification-time">{{ formatDate(notification.createdAt) }}</span>
          </div>
          <p class="notification-message">{{ notification.message }}</p>

          <div class="notification-actions">
            <button v-if="notification.targetType === NotificationTargetType.RegistrationReview && notification.targetId"
              class="notification-link" @click.stop="goToRegistration(notification)">
              <i class="pi pi-external-link"></i> Открыть заявку на регистрацию
            </button>
            <button
              v-if="canAssignOrder && notification.title?.toLowerCase().includes('новый заказ') && notification.orderId && !notification.isOrderAssigned"
              class="assign-order-btn"
              @click.stop="handleAssignOrder(notification)"
            >
              <i class="pi pi-user-plus"></i>
              Взять в работу
            </button>

            <div
              v-if="notification.orderId"
              class="notification-link"
              @click.stop="goToOrder(notification)"
            >
              <i class="pi pi-external-link"></i>
              Перейти к заказу №{{ notification.orderId }}
            </div>
          </div>
        </div>
        <div v-if="!notification.isRead" class="unread-badge">
          <i class="pi pi-circle-fill"></i>
        </div>
      </div>
    </div>

    <div v-if="filteredNotifications.length > 0" class="pagination">
      <button
        class="page-btn"
        :disabled="currentPage === 1"
        @click="currentPage--"
      >
        <i class="pi pi-chevron-left"></i>
      </button>
      <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
      <button
        class="page-btn"
        :disabled="currentPage === totalPages"
        @click="currentPage++"
      >
        <i class="pi pi-chevron-right"></i>
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/modules/auth/store/auth.store";
import { usePermissionsStore } from "@/modules/auth/store/permissions.store";
import { useNotificationStore } from "../store/notification.store";
import { NotificationTargetType } from "../model/notification-target";
import { useToast } from "primevue/usetoast";
import { assignOrder } from "@/modules/orders/api/orders.service";

const router = useRouter();
const authStore = useAuthStore();
const permissionsStore = usePermissionsStore();
const notificationStore = useNotificationStore();
const toast = useToast();

const searchQuery = ref("");
const filterType = ref("all");
const currentPage = ref(1);
const itemsPerPage = 10;

function getUserId(user) {
  const userId = user?.id ?? user?.Id ?? null;
  return userId === null ? null : String(userId);
}

const canAssignOrder = computed(() => permissionsStore.hasAnyPermission(["AssignOrder"]));

const allNotifications = computed(() => notificationStore.notifications);
const loading = computed(() => notificationStore.loading);
const unreadCount = computed(() => notificationStore.unreadCount);
const readCount = computed(() => allNotifications.value.filter(n => n.isRead).length);
const totalCount = computed(() => allNotifications.value.length);

const filteredNotifications = computed(() => {
  let filtered = [...allNotifications.value];

  if (filterType.value === "unread") {
    filtered = filtered.filter(n => !n.isRead);
  } else if (filterType.value === "read") {
    filtered = filtered.filter(n => n.isRead);
  }

  if (searchQuery.value.trim()) {
    const query = searchQuery.value.toLowerCase();
    filtered = filtered.filter(n =>
      n.title?.toLowerCase().includes(query) ||
      n.message?.toLowerCase().includes(query)
    );
  }

  return filtered;
});

const totalPages = computed(() => Math.ceil(filteredNotifications.value.length / itemsPerPage));

const paginatedNotifications = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage;
  const end = start + itemsPerPage;
  return filteredNotifications.value.slice(start, end);
});

watch([searchQuery, filterType], () => {
  currentPage.value = 1;
});

watch(
  () => getUserId(authStore.user),
  async (currentUserId, previousUserId) => {
    if (currentUserId === previousUserId || currentUserId === null) {
      return;
    }

    await notificationStore.fetchNotifications();
  },
);

const markAllAsRead = async () => {
  await notificationStore.markAllAsRead();
};

const handleAssignOrder = async (notification) => {
  try {
    const response = await assignOrder(notification.orderId);
    if (response.status === 200) {
      toast.add({
        severity: "success",
        summary: "Успешно",
        detail: `Заказ №${notification.orderId} назначен на вас`,
        life: 3000,
      });
      await notificationStore.fetchNotifications();
      router.push(`/admin/orders/${notification.orderId}`);
    }
  } catch (error) {
    console.error("Ошибка при назначении заказа:", error);
    toast.add({
      severity: "error",
      summary: "Ошибка",
      detail: error.response?.data?.message || "Не удалось назначить заказ",
      life: 3000,
    });
  }
};

const goToRegistration = async (notification) => {
  if (!notification.isRead) await notificationStore.markAsRead(notification.id);
  router.push(`/admin/registrations/${encodeURIComponent(notification.targetId)}`);
};

const goToOrder = async (notification) => {
  if (!notification.isRead) {
    await notificationStore.markAsRead(notification.id);
  }
  const roles = authStore.user?.roles || [];
  const isAdminOrManager = roles.some(role => ["Admin", "Manager", "God"].includes(role));
  if (isAdminOrManager) {
    router.push(`/admin/orders/${notification.orderId}`);
  } else {
    router.push({ name: "orderDetails", params: { id: notification.orderId } });
  }
};

const getNotificationIcon = (title) => {
  if (!title) return "pi pi-bell";
  if (title.includes("новый заказ") || title.includes("🛒")) return "pi pi-shopping-cart";
  if (title.includes("назначен")) return "pi pi-user-plus";
  if (title.includes("статус") || title.includes("🟡") || title.includes("🚚")) return "pi pi-refresh";
  if (title.includes("выполнен") || title.includes("✅")) return "pi pi-check-circle";
  if (title.includes("закрыт") || title.includes("❌")) return "pi pi-times-circle";
  return "pi pi-bell";
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const now = new Date();
  const diff = now - date;
  if (diff < 60 * 1000) return "только что";
  if (diff < 60 * 60 * 1000) return `${Math.floor(diff / 60000)} мин назад`;
  if (diff < 24 * 60 * 60 * 1000) return `${Math.floor(diff / 3600000)} ч назад`;
  if (diff < 7 * 24 * 60 * 60 * 1000) return `${Math.floor(diff / (24 * 3600000))} дн назад`;
  return date.toLocaleDateString("ru-RU");
};

onMounted(async () => {
  await notificationStore.fetchNotifications();
});
</script>

<style scoped>
.notifications-page {
  max-width: 900px;
  margin: 0 auto;
  padding: 24px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 28px;
  flex-wrap: wrap;
  gap: 16px;
}

.page-header h1 {
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  background: linear-gradient(135deg, #1a1a1a 0%, #333 100%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.mark-all-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px;
  background: #f8f8f8;
  border: 1px solid #e5e5e5;
  border-radius: 40px;
  color: #555;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.25s ease;
}

.mark-all-btn:hover {
  background: #c41217;
  border-color: #c41217;
  color: white;
  transform: translateY(-1px);
}

.filters-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.search-field {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 14px;
  border: 1px solid #e5e7eb;
  border-radius: 40px;
  background: white;
  transition: all 0.2s;
}

.search-field:focus-within {
  border-color: #c41217;
  box-shadow: 0 0 0 2px rgba(196, 18, 23, 0.1);
}

.search-field i {
  color: #9ca3af;
}

.search-input {
  flex: 1;
  padding: 10px 0;
  border: none;
  background: transparent;
  font-size: 14px;
  outline: none;
}

.filter-buttons {
  display: flex;
  gap: 8px;
}

.filter-btn {
  padding: 8px 16px;
  background: #f8f8f8;
  border: 1px solid #e5e5e5;
  border-radius: 40px;
  color: #666;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.filter-btn:hover {
  background: #f0f0f0;
}

.filter-btn.active {
  background: #c41217;
  border-color: #c41217;
  color: white;
}

.loading-state,
.empty-state {
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 24px;
  border: 1px solid #f0f0f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);
}

.loading-state i,
.empty-state i {
  font-size: 56px;
  color: #ddd;
  margin-bottom: 20px;
}

.empty-state h3 {
  margin: 0 0 8px;
  font-size: 20px;
  font-weight: 600;
  color: #333;
}

.empty-state p {
  color: #999;
  margin: 0;
  font-size: 14px;
}

.notifications-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.notification-card {
  display: flex;
  gap: 18px;
  padding: 20px 24px;
  background: white;
  border: 1px solid #ededed;
  border-radius: 20px;
  transition: all 0.25s ease;
  position: relative;
}

.notification-card:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
  transform: translateY(-2px);
  border-color: #e0e0e0;
}

.notification-card.unread {
  background: linear-gradient(135deg, #fff9f9 0%, #ffffff 100%);
  border-left: 4px solid #c41217;
}

.notification-icon {
  flex-shrink: 0;
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fafafa;
  border-radius: 16px;
  color: #c41217;
  font-size: 24px;
  transition: all 0.2s;
}

.notification-card:hover .notification-icon {
  background: #fff0f0;
}

.notification-content {
  flex: 1;
}

.notification-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 10px;
  flex-wrap: wrap;
  gap: 10px;
}

.notification-title {
  font-weight: 700;
  font-size: 16px;
  color: #1a1a1a;
}

.notification-time {
  font-size: 12px;
  color: #aaa;
  font-weight: 400;
}

.notification-message {
  margin: 0 0 12px;
  font-size: 14px;
  color: #666;
  line-height: 1.5;
}

.notification-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  margin-top: 8px;
}

.assign-order-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 18px;
  background: #c41217;
  border: none;
  border-radius: 40px;
  color: white;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.assign-order-btn:hover {
  background: #a00e12;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(196, 18, 23, 0.3);
}

.assign-order-btn i {
  font-size: 14px;
}

.notification-link {
  font-size: 13px;
  color: #c41217;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 40px;
  transition: all 0.2s;
}

.notification-link:hover {
  background: #fff5f5;
  color: #a00e12;
}

.unread-badge {
  position: absolute;
  top: 20px;
  right: 20px;
  color: #c41217;
  font-size: 10px;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 24px;
  padding: 16px 0;
}

.page-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid #e5e5e5;
  background: white;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.page-btn:hover:not(:disabled) {
  background: #c41217;
  border-color: #c41217;
  color: white;
}

.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-info {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

@media (max-width: 640px) {
  .notifications-page {
    padding: 16px;
  }

  .page-header h1 {
    font-size: 22px;
  }

  .filters-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-buttons {
    justify-content: stretch;
  }

  .filter-btn {
    flex: 1;
    text-align: center;
  }

  .notification-card {
    padding: 16px;
    gap: 12px;
  }

  .notification-icon {
    width: 44px;
    height: 44px;
    font-size: 20px;
  }

  .notification-header {
    flex-direction: column;
    gap: 4px;
  }

  .notification-actions {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .assign-order-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
